<template>
    <div class="photo-item">
        <button
            class="photo-item__delete"
            type="button"
            @click.stop="$emit('remove', item.localId)"
            title="Удалить"
            :aria-label="`Удалить: ${fileName}`"
        >
            <i class="fas fa-times" aria-hidden="true"></i>
        </button>

        <div class="photo-item__preview">
            <img v-if="photoData" :src="photoData" :alt="fileName" class="photo-item__img" />

            <div v-else class="photo-item__placeholder">
                <!-- uploading: показываем спиннер (а не готовую иконку) -->
                <div v-if="['uploading', 'ready'].includes(item.status)" class="photo-item__status photo-item__status--uploading">
                    <i class="fas fa-spinner fa-spin photo-item__status-icon" aria-hidden="true"></i>
                    <span class="photo-item__format">Загрузка</span>
                </div>

                <!-- ready: показываем цветную иконку типа + подпись формата -->
                <div
                    v-else-if="item.status === 'ready'"
                    class="photo-item__status photo-item__status--ready"
                    :class="isVideo ? 'photo-item__status--video' : 'photo-item__status--image'"
                >
                    <i
                        class="fas photo-item__type-icon"
                        :class="item.iconClass"
                        aria-hidden="true"
                    ></i>
                    <span class="photo-item__format">{{ item.formatLabel }}</span>
                </div>

                <!-- error: красный статус -->
                <div v-else-if="item.status === 'error'" class="photo-item__status photo-item__status--error">
                    <i class="fas fa-exclamation-triangle photo-item__status-icon" aria-hidden="true"></i>
                    <span class="photo-item__format">Ошибка</span>
                </div>
            </div>
        </div>

        <div
            ref="nameEl"
            class="photo-item__name"
            :title="isNameTruncated ? fileName : null"
        >
            {{ fileName }}
        </div>
    </div>
</template>
<script>
import api from '../../utils/api';
export default {
    name: 'PhotoCard',
    props: {
        item: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            photoData: null,
            objectUrl: null,
            activePreviewUrl: null,
            isNameTruncated: false
        }
    },
    computed: {
        fileName() {
            const name = this.item && this.item.fileMeta && this.item.fileMeta.name;
            return name || 'file';
        },
        isVideo() {
            return Boolean(this.item && this.item.iconClass && String(this.item.iconClass).includes('fa-video'));
        }
    },
    methods: {
        updateNameTruncation() {
            this.$nextTick(() => {
                const el = this.$refs.nameEl;
                if (!el) return;

                // Для 2-х строк line-clamp корректнее проверять высоту/скролл,
                // т.к. scrollWidth не отражает переносы строк.
                this.isNameTruncated = el.scrollHeight > el.clientHeight + 1;
            });
        },
        async getPhotoData(previewUrl) {
            if (!previewUrl) return;

            this.activePreviewUrl = previewUrl;

            // Освобождаем предыдущий object URL, чтобы не накапливать в памяти
            if (this.objectUrl) {
                URL.revokeObjectURL(this.objectUrl);
                this.objectUrl = null;
            }

            this.photoData = null;

            try {
                const res = await api.get(previewUrl, { responseType: 'blob', params: { ignoreModal: true } });
                const url = URL.createObjectURL(res.data);

                // Защита от гонок: если за время запроса previewUrl успел измениться,
                // не перезаписываем карточку “устаревшим” изображением.
                if (previewUrl !== this.activePreviewUrl) {
                    URL.revokeObjectURL(url);
                    return;
                }

                this.objectUrl = url;
                this.photoData = url;
            } catch (err) {
                console.error(err);
            }
        }
    },
    watch: {
        fileName: {
            immediate: true,
            handler() {
                this.updateNameTruncation();
            }
        },
        'item.tempPhoto.previewUrl': {
            immediate: true,
            handler(newVal) {
                // Загружаем картинку только если URL есть и статус не error
                if (newVal && this.item && this.item.status !== 'error') {
                    this.getPhotoData(newVal);
                } else {
                    // Если превью пропало (или ошибка) — очистим картинку
                    if (this.objectUrl) {
                        URL.revokeObjectURL(this.objectUrl);
                        this.objectUrl = null;
                    }
                    this.photoData = null;
                    this.activePreviewUrl = null;
                }
            }
        }
    },
    mounted() {
        this.updateNameTruncation();
        window.addEventListener('resize', this.updateNameTruncation, { passive: true });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateNameTruncation);
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
        }
    }
}
</script>
<style lang="scss" scoped>
.photo-item {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    background: #f1f3f5;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

    // Снимаем “ошибки слоёв”: фон/оверлеи не должны перекрывать кнопку закрытия
    &__preview {
        position: relative;
        width: 100%;
        aspect-ratio: 1 / 1;
        background: #f1f3f5;
    }

    &__img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    &__placeholder {
        position: absolute;
        inset: 0;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 10px;
    }

    &__status {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 6px;
        border-radius: 10px;
        background: linear-gradient(135deg, #f1f3f5, #e9ecef);
        color: #495057;

        &--uploading {
            --status: #0d6efd;
            background: linear-gradient(135deg, rgba(13, 110, 253, 0.18), rgba(13, 110, 253, 0.06));
        }

        &--image {
            --status: #0d6efd;
            background: linear-gradient(135deg, rgba(13, 110, 253, 0.22), rgba(13, 110, 253, 0.06));
        }

        &--video {
            --status: #f97316;
            background: linear-gradient(135deg, rgba(249, 115, 22, 0.22), rgba(249, 115, 22, 0.06));
        }

        &--error {
            --status: #dc3545;
            background: linear-gradient(135deg, rgba(220, 53, 69, 0.18), rgba(220, 53, 69, 0.06));
        }
    }

    &__status-icon {
        font-size: 28px;
        color: var(--status, #6c757d);
        filter: drop-shadow(0 3px 10px rgba(0, 0, 0, 0.12));
    }

    &__type-icon {
        font-size: 30px;
        color: var(--status, #6c757d);
        filter: drop-shadow(0 3px 10px rgba(0, 0, 0, 0.12));
    }

    &__format {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: rgba(0, 0, 0, 0.55);
        background: rgba(255, 255, 255, 0.7);
        padding: 4px 8px;
        border-radius: 999px;
        backdrop-filter: blur(6px);
    }

    &__delete {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 5; // важнее, чем &__placeholder

        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.35);
        color: #fff;
        border: 1px solid rgba(255, 255, 255, 0.35);
        cursor: pointer;

        display: flex;
        align-items: center;
        justify-content: center;

        transition: transform 0.15s ease, background 0.2s ease, border-color 0.2s ease;

        i {
            font-size: 14px;
            line-height: 1;
        }

        &:hover {
            background: rgba(220, 53, 69, 0.92);
            border-color: rgba(255, 255, 255, 0.45);
            transform: translateY(-1px);
        }

        &:active {
            transform: translateY(0);
        }
    }

    &__name {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 2;

        font-size: 11px;
        font-weight: 600;
        color: #6c757d;     
        text-align: center;

        // Длинные имена: читаемо в 2 строки, без “тотального” обрезания
        white-space: normal;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        word-break: break-word;
        overflow-wrap: anywhere;
        line-height: 1.25;
        max-height: calc(1.25em * 2 + 16px); // 2 строки + вертикальные паддинги
        background: rgba(255, 255, 255, 0.88);
        border-top: 1px solid rgba(0, 0, 0, 0.04);
    }
}

@media (max-width: 576px) {
    .photo-item {
        border-radius: 10px;

        &__delete {
            top: 7px;
            right: 7px;
            width: 32px;
            height: 32px;

            i {
                font-size: 15px;
            }
        }

        &__status-icon {
            font-size: 26px;
        }

        &__type-icon {
            font-size: 28px;
        }

        &__format {
            font-size: 10px;
            padding: 4px 7px;
        }

        &__name {
            font-size: 11px;
            padding: 8px 9px;
            line-height: 1.25;
            max-height: calc(1.25em * 2 + 16px);
        }
    }
}
</style>